import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { SvgMatType } from "src/app/constants/icons";
import { MemberPaths } from "src/app/constants/routes";
import { BenefitWizardType } from "src/app/constants/wizard-panels";
import { BenefitSummaryList } from "src/app/models/benefit-summary";
import { BenefitWizardControlPanel } from "src/app/models/enrollment-benefit-wizard";
import { EnrollmentBenefitCategory, EnrollmentBenefitCategoryItem, EnrollmentWaiveBenefits } from "src/app/models/enrollment-category";
import { CheckoutComplete } from "src/app/models/enrollment-checkout";
import { BenefitCategories, EnrollmentCategoryOptionManager, EnrollmentCategoryOptions, MemberBenefitFlyout } from "src/app/models/enrollment-option";
import { ResourceLink } from "src/app/models/resource-link";
import { BenefitService } from "src/app/services/benefit.service";
import { EnrollmentManagerService } from "src/app/services/enrollment-manager.service";
import { HelperService } from "src/app/services/helper.service";
import { Document } from "src/app/models/document";
import { DialogDeductionSummaryComponent } from "src/app/components/shared/dialog/deduction-summary.component";
import { DialogCardResourceComponent } from "src/app/components/shared/dialog/card-resource.component";
import { WidgetError } from "src/app/models/widget";
import { ResourcesDialogModel } from "src/app/models/dialog";
import { MemberService } from "src/app/services/member.service";

@Component({
    selector: 'mwp-benefit-enrollment-manager',
    templateUrl: './enrollment-manager.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnrollmentBenefitManagerComponent implements OnInit, OnDestroy {
    public svgType = SvgMatType;
    public selectedEnrollmentBenefitCategoryItem: EnrollmentBenefitCategoryItem;
    public selectedMemberBenefitFlyout: MemberBenefitFlyout;
    public selectedBenefitCategories: BenefitCategories;
    public selectedEnrollmentCategoryOptions: EnrollmentCategoryOptions;
    public currentBenefitsList: BenefitSummaryList;
    public nextEnrollmentBenefitCategoryItem: EnrollmentBenefitCategoryItem;
    public checkoutComplete: CheckoutComplete;

    // Page Control
    public isLoadingCategory: boolean;
    public isFauxCategory: boolean;
    public isCategoryHTMLAvailable: boolean;
    public displayCategoryManagerControl: boolean;
    public displayBenefitWizardControl: boolean;
    public displayBenefitWidgetControl: boolean;
    public displayCategoryFinalizeControl: boolean;
    public displayCheckoutControl: boolean;
    public displayTimeline: boolean;
    public toggleCategoryIntroduction: boolean;
    public categroyResources: ResourceLink[];
    public companyResources: ResourceLink[];
    public displayResources: boolean;

    constructor(
        public enrollmentManagerService: EnrollmentManagerService,
        public dialog: MatDialog,
        private helperService: HelperService,
        private benefitService: BenefitService,
        private router: Router,
        private memberService: MemberService,
    ) {
        this.enrollmentManagerService.$TimelineSelectedCategory.subscribe((category: EnrollmentBenefitCategoryItem) => {
            this.isLoadingCategory = true;
            this.enrollmentManagerService.manager.BenefitWizardType = null;
            this.enrollmentManagerService.manager.selectedBenefitName = null;
            this.selectedCategory(category);
        });

        this.enrollmentManagerService.$CancelBenefitWizard.subscribe((val: boolean) => {
            if (val && this.enrollmentManagerService.manager.selectedCategoryId != this.enrollmentManagerService.checkoutItem.id)
                this.cancelWizard();
        });

        this.enrollmentManagerService.$EnrollmentComplete.subscribe((checkoutComplete: CheckoutComplete) => {
            if (checkoutComplete) {
                checkoutComplete.title = `${this.enrollmentManagerService.manager.enrollmentMode.name} - ${checkoutComplete.title}`;
                this.checkoutComplete = checkoutComplete;
                this.benefitService.resetMyBenefitsList();
                this.enrollmentManagerService.resetEnrollmentManagerStore();
                this.memberService.resetMessageCenter();
            }
        });
    }

    ngOnInit(): void {
        if (this.enrollmentManagerService.manager?.benefitCategories?.length > 0) {
            this.categorySelection();
        } else {
            this.enrollmentManagerService.getEnrollmentCategoires().subscribe((response: EnrollmentBenefitCategory) => {
                this.enrollmentManagerService.manager = this.helperService.convertObjectIntoObject(response, this.enrollmentManagerService.manager);
                this.enrollmentManagerService.manager.isCheckoutEnabled = response.isCheckoutEnabled;
                this.enrollmentManagerService.setCacheManager();
                if (!this.enrollmentManagerService.manager.selectedCategoryId)
                    this.selectedCategory(this.enrollmentManagerService.manager.benefitCategories[0]);
                else
                    this.categorySelection();
            });
        }
    }

    ngOnDestroy(): void {
        this.checkoutComplete = null;
    }

    public selectedCategory(item: EnrollmentBenefitCategoryItem): void {
        if (item && this.enrollmentManagerService.manager.selectedCategoryId != item.id) {
            this.enrollmentManagerService.manager.selectedCategoryId = item.id;
            this.enrollmentManagerService.setCacheManager();
            this.categorySelection();
        }
    }

    private categorySelection(resetTimeLine: boolean = true): void {
        this.resetDisplay();
        this.selectedEnrollmentBenefitCategoryItem = this.enrollmentManagerService.manager.benefitCategories.find(bc => bc.id == this.enrollmentManagerService.manager.selectedCategoryId);
        this.enrollmentManagerService.manager.selectedCategoryName = this.selectedEnrollmentBenefitCategoryItem?.name;

        if (this.enrollmentManagerService.manager.selectedCategoryId == this.enrollmentManagerService.checkoutItem.id) {
            this.isLoadingCategory = true;
            if (resetTimeLine) {
                this.enrollmentManagerService.$UpdateTimeline.next(this.enrollmentManagerService.manager.selectedCategoryId);
                this.displayTimeline = true;
            }
            this.enrollmentManagerService.benefitWizardControlPanel = new BenefitWizardControlPanel();
            this.enrollmentManagerService.benefitWizardControlPanel.wizardType = BenefitWizardType.Checkout;
            this.enrollmentManagerService.manager.BenefitWizardType = BenefitWizardType.Checkout;
            this.isLoadingCategory = false;
            this.displayCheckoutControl = true;
        }
        else {
            this.isLoadingCategory = true;
            if (resetTimeLine) {
                this.enrollmentManagerService.$UpdateTimeline.next(this.enrollmentManagerService.manager.selectedCategoryId);
                this.displayTimeline = true;
            }
            this.displayCategoryManagerControl = false;
            this.loadCategoryOptions();
        }
    }

    private loadCategoryOptions(): void {
        this.enrollmentManagerService.getEnrollmentCategoryOptions(this.enrollmentManagerService.manager.selectedCategoryId).subscribe((response: EnrollmentCategoryOptionManager) => {
            this.selectedEnrollmentCategoryOptions = response.enrollmentCategoryOptionsModel;
            this.selectedMemberBenefitFlyout = response.memberBenefitFlyoutModel;
            this.selectedBenefitCategories = response.benefitCategoriesModel;
            this.currentBenefitsList = response.enrollmentCategoryOptionsModel.genericBenefitSummaryModel;
            this.isFauxCategory = this.selectedEnrollmentCategoryOptions.isFauxCategory;
            this.isCategoryHTMLAvailable = this.selectedEnrollmentCategoryOptions.isHTMLAvailable;

            this.categroyResources = new Array<ResourceLink>();
            this.companyResources = new Array<ResourceLink>();
            this.displayResources = false;
            if (this.selectedBenefitCategories?.benefitCategoriesModelDetails[0]?.benefitDocuments.length > 0) {
                this.displayResources = true;
                this.selectedBenefitCategories.benefitCategoriesModelDetails[0].benefitDocuments.forEach((cd: Document) => {
                    this.categroyResources.push(this.helperService.convertObjectIntoObject(cd, new ResourceLink()));
                });
            }
            if (this.selectedBenefitCategories?.companyDocuments.length > 0) {
                this.displayResources = true;
                this.selectedBenefitCategories.companyDocuments.forEach((cd: Document) => {
                    this.companyResources.push(this.helperService.convertObjectIntoObject(cd, new ResourceLink()));
                });
            }

            this.displayCategoryManagerControl = true;
            this.isLoadingCategory = false;
        });
    }

    private resetDisplay(): void {
        this.displayCategoryManagerControl =
            this.displayBenefitWizardControl =
            this.displayCategoryFinalizeControl =
            this.displayCheckoutControl =
            this.displayBenefitWidgetControl =
            this.isFauxCategory = false;
    }

    public returnToCategoryIntorudction(): void {
        this.toggleCategoryIntroduction = true;
    }

    public wizardIntialize(event: BenefitWizardType): void {
        this.enrollmentManagerService.benefitWizardControlPanel = new BenefitWizardControlPanel();
        this.enrollmentManagerService.benefitWizardControlPanel.wizardType = event;
        this.enrollmentManagerService.manager.BenefitWizardType = event;

        switch (this.enrollmentManagerService.benefitWizardControlPanel.wizardType) {
            case BenefitWizardType.Checkout:
                this.displayCategoryManagerControl =
                    this.displayCategoryFinalizeControl =
                    this.displayBenefitWidgetControl =
                    this.displayBenefitWizardControl = false;
                this.displayCheckoutControl = true;
                break;

            case BenefitWizardType.Widget:
                this.displayCategoryManagerControl =
                    this.displayCategoryFinalizeControl =
                    this.displayBenefitWizardControl =
                    this.displayCheckoutControl = false;
                this.displayBenefitWidgetControl = true;
                break;

            default:
                if (event == BenefitWizardType.WaiveBenefit) {
                    let l_WavieBenefits = new EnrollmentWaiveBenefits();
                    l_WavieBenefits.benefitNumber = this.selectedEnrollmentCategoryOptions.waiveBenefitNumber;
                    this.enrollmentManagerService.waiveBenefits = l_WavieBenefits;
                }

                this.displayCategoryManagerControl =
                    this.displayCategoryFinalizeControl =
                    this.displayBenefitWidgetControl =
                    this.displayCheckoutControl = false;
                this.displayBenefitWizardControl = true;
                break;
        }
    }

    public cancelWizard(): void {
        this.displayBenefitWizardControl =
            this.displayCategoryFinalizeControl =
            this.displayBenefitWidgetControl = false;
        this.enrollmentManagerService.manager.selectedBenefitName = null;
        this.enrollmentManagerService.manager.BenefitWizardType = null;
        this.enrollmentManagerService.benefitWizardControlPanel = null;
        this.enrollmentManagerService.cancelHideIntro = true;
        this.enrollmentManagerService.setCacheManager();
        this.displayCategoryManagerControl = true;
    }

    public buildCategoryFinalize(event: EnrollmentBenefitCategory): void {
        this.selectedMemberBenefitFlyout = event.memberBenefitFlyout;
        this.enrollmentManagerService.manager = this.helperService.convertObjectIntoObject(event, this.enrollmentManagerService.manager);
        this.enrollmentManagerService.setCacheManager();
        this.enrollmentManagerService.$UpdateTimeline.next(this.enrollmentManagerService.manager.selectedCategoryId);
        this.displayTimeline = true;
        this.nextEnrollmentBenefitCategoryItem = null;

        this.setNextCategory();

        this.displayBenefitWidgetControl = false;
        this.displayCategoryFinalizeControl = true;
    }

    public buildWidgetErrorCategoryFinalizePanel(event: WidgetError): void {
        this.enrollmentManagerService.manager.widgetErrorModel = event;
        this.enrollmentManagerService.setCacheManager();
        this.enrollmentManagerService.$UpdateTimeline.next(this.enrollmentManagerService.manager.selectedCategoryId);
        this.displayTimeline = true;
        this.nextEnrollmentBenefitCategoryItem = null;

        this.setNextCategory();

        this.displayBenefitWidgetControl = false;
        this.displayCategoryFinalizeControl = true;
    }

    private setNextCategory(): void {
        let l_LoadNextCategory: boolean;
        for (var bc of this.enrollmentManagerService.manager.benefitCategories) {
            if (l_LoadNextCategory) {
                this.nextEnrollmentBenefitCategoryItem = bc;
                break;
            }

            l_LoadNextCategory = bc.id == this.enrollmentManagerService.manager.selectedCategoryId;
        }

        if (!this.nextEnrollmentBenefitCategoryItem && this.enrollmentManagerService.manager.isCheckoutEnabled)
            this.nextEnrollmentBenefitCategoryItem = this.enrollmentManagerService.checkoutItem;
    }

    public reloadCurrentCategoryBenefits(): void {
        this.resetFinalize();
        this.isLoadingCategory = true;
        this.loadCategoryOptions();
    }

    public loadNextCatgory(): void {
        if (!this.nextEnrollmentBenefitCategoryItem)
            this.setNextCategory();

        this.selectedCategory(this.nextEnrollmentBenefitCategoryItem);
        this.resetFinalize();
    }

    public nextCategoryAfterFaux(): void {
        const l_CurrentIndex: number = this.enrollmentManagerService.manager.benefitCategories.findIndex(bc => bc.id == this.enrollmentManagerService.manager.selectedCategoryId);
        let l_CurrentBenefitCategory: EnrollmentBenefitCategoryItem = this.enrollmentManagerService.manager.benefitCategories[l_CurrentIndex + 1];

        if (!l_CurrentBenefitCategory) {
            if (this.enrollmentManagerService.manager.isCheckoutEnabled) {
                l_CurrentBenefitCategory = this.enrollmentManagerService.checkoutItem;
            } else {
                const l_NextRequiredIndex: number = this.enrollmentManagerService.manager.benefitCategories.findIndex(bc => bc.isRequired == true && bc.isCompleted == false);
                l_CurrentBenefitCategory = this.enrollmentManagerService.manager.benefitCategories[l_NextRequiredIndex];
            }
        }
        
        this.selectedCategory(l_CurrentBenefitCategory);
    }

    public initializeCheckout(): void {
        this.selectedCategory(this.enrollmentManagerService.checkoutItem);
        this.resetFinalize();
    }

    private resetFinalize(): void {
        this.enrollmentManagerService.manager.BenefitWizardType = null;
        this.nextEnrollmentBenefitCategoryItem = null;
        this.displayBenefitWizardControl = false;
        this.displayCategoryFinalizeControl = false;
    }

    public openDeductionSummaryDialog(): void {
        this.dialog.open(DialogDeductionSummaryComponent, { data: this.selectedMemberBenefitFlyout, panelClass: 'mwp-modal-override-800' });
    }

    public openResourceSummaryDialog(): void {
        const l_ResourcesDialogModel: ResourcesDialogModel = new ResourcesDialogModel();
        l_ResourcesDialogModel.categroyResources = this.categroyResources,
        l_ResourcesDialogModel.companyResources = this.companyResources

        this.dialog.open(DialogCardResourceComponent, { data: l_ResourcesDialogModel, panelClass: 'mwp-modal-override-600' });
    }

    public loadingComplete(): void {
        this.isLoadingCategory = false;
    }

    public returnToBenefitsDashboard(): void {
        this.router.navigateByUrl(MemberPaths.BenefitsDashboard);
    }
}

